import { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n";
import { SOLUTIONS } from "../content/solutions";
import { SECTORS } from "../content/sectors";
import { Logo } from "./Logo";
import { api, apiError } from "../lib/api";
import { Turnstile } from "./Turnstile";
import { Linkedin, Twitter, Facebook, Instagram, Youtube, MapPin, Phone, Mail } from "lucide-react";

const SOCIAL_ICONS = { linkedin: Linkedin, twitter: Twitter, facebook: Facebook, instagram: Instagram, youtube: Youtube };

export const Footer = ({ boot }) => {
  const { lang, t, p } = useLang();
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const contact = boot?.settings?.contact || {};
  const socials = Object.entries(boot?.settings?.social_links || {}).filter(([, v]) => v);

  const subscribe = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg("");
    try {
      const res = await api.post("/public/newsletter", { email, locale: lang, turnstile_token: token, honeypot: "" });
      setMsg(res.data.message); setEmail("");
    } catch (err) { setMsg(apiError(err, t.forms.error)); }
    finally { setBusy(false); }
  };

  const col = "text-sm text-white/60 hover:text-[#FF9900] transition-colors";

  return (
    <footer className="bg-[#08090A] border-t border-white/10 text-white" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-14 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Logo />
            <p className="mt-5 text-sm text-white/60 leading-relaxed max-w-xs">{t.footer.mission}</p>
            <form onSubmit={subscribe} className="mt-6" data-testid="newsletter-form">
              <label htmlFor="nl-email" className="text-xs font-bold tracking-widest uppercase text-[#FF9900]">{t.footer.newsletter}</label>
              <div className="mt-2 flex gap-2">
                <input id="nl-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.footer.newsletterPlaceholder} data-testid="newsletter-email-input"
                  className="flex-1 min-w-0 bg-white/5 border border-white/15 rounded-full px-4 py-2.5 text-sm placeholder:text-white/35 focus:outline-none focus:border-[#FF6600]" />
                <button type="submit" disabled={busy} data-testid="newsletter-submit-button"
                  className="shrink-0 bg-[#FF6600] hover:bg-[#FF9900] text-[#08090A] text-sm font-black rounded-full px-5 transition-colors disabled:opacity-50">
                  {t.footer.newsletterBtn}
                </button>
              </div>
              <div className="mt-2"><Turnstile action="newsletter" onToken={setToken} compact /></div>
              {msg && <p className="mt-2 text-xs text-[#FFC000]" role="status" data-testid="newsletter-status">{msg}</p>}
            </form>
            {socials.length > 0 && (
              <div className="mt-6 flex gap-3">
                {socials.map(([k, url]) => {
                  const Icon = SOCIAL_ICONS[k];
                  return Icon ? (
                    <a key={k} href={url} target="_blank" rel="noopener noreferrer" aria-label={k} data-testid={`social-${k}`}
                      className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/60 hover:text-[#FF6600] hover:border-[#FF6600] transition-colors">
                      <Icon size={16} />
                    </a>
                  ) : null;
                })}
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            <h3 className="text-xs font-bold tracking-widest uppercase text-white/40 mb-4">{t.footer.solutions}</h3>
            <ul className="space-y-2.5">
              {SOLUTIONS.map((s) => <li key={s.slug}><Link className={col} to={p(`solutions/${s.slug}`)}>{s[lang].title.split("—")[0].split(",")[0]}</Link></li>)}
            </ul>
          </div>
          <div className="lg:col-span-2">
            <h3 className="text-xs font-bold tracking-widest uppercase text-white/40 mb-4">{t.footer.sectors}</h3>
            <ul className="space-y-2.5">
              {SECTORS.map((s) => <li key={s.slug}><Link className={col} to={p(`secteurs/${s.slug}`)}>{s[lang].title}</Link></li>)}
            </ul>
          </div>
          <div className="lg:col-span-2">
            <h3 className="text-xs font-bold tracking-widest uppercase text-white/40 mb-4">{t.footer.company}</h3>
            <ul className="space-y-2.5">
              <li><Link className={col} to={p("a-propos")}>{t.nav.about}</Link></li>
              <li><Link className={col} to={p("infrastructure")}>{t.nav.infrastructure}</Link></li>
              <li><Link className={col} to={p("connectivite-rurale")}>{t.nav.rural}</Link></li>
              <li><Link className={col} to={p("partenaires")}>{t.nav.partners}</Link></li>
              <li><Link className={col} to={p("actualites")}>{t.nav.news}</Link></li>
              <li><Link className={col} to={p("carrieres")}>{t.nav.careers}</Link></li>
              <li><Link className={col} to={p("contact")}>{t.nav.contact}</Link></li>
            </ul>
          </div>
          <div className="lg:col-span-2">
            <h3 className="text-xs font-bold tracking-widest uppercase text-white/40 mb-4">{t.footer.contact}</h3>
            <ul className="space-y-3 text-sm text-white/60">
              <li className="flex gap-2"><MapPin size={15} className="text-[#FF6600] shrink-0 mt-0.5" /><span className="whitespace-pre-line">{(lang === "fr" ? contact.hq_address_fr : contact.hq_address_en) || ""}</span></li>
              <li className="flex gap-2 items-center"><Phone size={15} className="text-[#FF6600] shrink-0" /><a className={col} href={`tel:${(contact.phone_drc || "").replace(/\s/g, "")}`}>{contact.phone_drc}</a></li>
              <li className="flex gap-2 items-center"><Mail size={15} className="text-[#FF6600] shrink-0" /><a className={col} href={`mailto:${contact.email || "info@bulatelecoms.com"}`}>{contact.email || "info@bulatelecoms.com"}</a></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-white/45">
            <Link className="hover:text-[#FF9900]" to={p("mentions-legales")}>{t.nav.legal}</Link>
            <Link className="hover:text-[#FF9900]" to={p("confidentialite")}>{t.nav.privacy}</Link>
            <Link className="hover:text-[#FF9900]" to={p("cookies")}>{t.nav.cookies}</Link>
            <button className="hover:text-[#FF9900]" data-testid="footer-cookie-preferences"
              onClick={() => window.dispatchEvent(new Event("bt-open-cookies"))}>{t.cookiesBanner.manage}</button>
          </div>
          <p className="text-xs text-white/45">{t.footer.rights.replace("{year}", new Date().getFullYear())}</p>
          <p className="text-xs font-bold text-[#FF6600]">{t.footer.tagline}</p>
        </div>
      </div>
    </footer>
  );
};
